import { type LucideIcon, Upload } from "lucide-react";
import type { DragEvent, ReactNode } from "react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@krea/shared/design-system/Button";
import { cn } from "@krea/shared/utils";

interface IFileDropAreaProps {
  /** Files chosen EITHER by dropping on the area or through the picker it
   *  opens — the caller cannot tell the two apart, and should not need to. */
  onFiles: (files: File[]) => void;
  /** The line a customer reads first, e.g. "Upload the PDF here". Also names
   *  the file input for assistive tech, which has no visible label of its own. */
  label: string;
  /** Optional second line — how to do it. Defaults to the shared "by dragging
   *  or clicking here", which the component renders responsively; pass a value
   *  only where that is wrong for the question. */
  hint?: string;
  /** Which file types are accepted, in words. Defaults to the same generic
   *  message customer-features' `FileInput` shows; pass a specific one where
   *  the question is narrower, or `null` to leave the line out. */
  acceptedFileTypesMessage?: string | null;
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  /** For when files already exist: a smaller one-row drop box so the files
   *  stay near the top but the drop target stays visible (KREA-4620 — the
   *  button was being missed). */
  compact?: boolean;
  /** Renders a ghost button instead of a drop target, for toolbars
   *  (TeamPhotoComposer). */
  asButton?: boolean;
  /** The label in `compact` and `asButton` modes, e.g. "Add more files". */
  compactLabel?: string;
  /** Leading icon in `compact` and `asButton` modes, to match sibling icon
   *  buttons. `compact` falls back to the upload icon. */
  compactIcon?: LucideIcon;
  /** Anything extra inside the area. NOT a trigger — the whole area is the
   *  trigger, so a nested button would open the picker twice. */
  children?: ReactNode;
  className?: string;
  /** `data-test-id` for the hidden file input, for e2e suites that upload
   *  through it directly. */
  inputTestId?: string;
}

// In button mode the visible Button is the keyboard control; leaving the
// sr-only input tabbable would add a second, invisible tab stop before it.
const buttonModeInputProps = (asButton?: boolean) =>
  asButton ? ({ tabIndex: -1, "aria-hidden": true } as const) : undefined;

/**
 * A drag-and-drop target that is ALSO the click target, wrapping its own file
 * input.
 *
 * The input is `sr-only` rather than `invisible` on purpose: `visibility:
 * hidden` takes a control out of the tab order, and this input is the keyboard
 * path now that the area carries no button. `aria-label` names it, since the
 * visible label belongs to the area rather than the control.
 *
 * The standard drag-and-drop uploader — reach for it (or `FileInput`,
 * which is built on it) rather than hand-rolling another drop target.
 *
 * The default hint and accepted-types copy read `iaq.fileUpload.*` from the
 * customer-features `common` namespace, the same way `file-item` reads
 * `commons.*`. Apps that do not load that namespace (lender) must pass `hint`
 * and `acceptedFileTypesMessage` explicitly or they render raw keys.
 */
export const FileDropArea = ({
  onFiles,
  label,
  hint,
  acceptedFileTypesMessage,
  accept,
  multiple = true,
  disabled,
  compact,
  asButton,
  compactLabel,
  compactIcon: CompactIcon,
  children,
  className,
  inputTestId,
}: IFileDropAreaProps) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const CompactAreaIcon = CompactIcon ?? Upload;
  // The input names itself after whatever label is actually on screen.
  const visibleLabel = compact || asButton ? (compactLabel ?? label) : label;

  const openPicker = () => {
    if (!disabled) inputRef.current?.click();
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    // Without preventDefault the browser navigates away to the dropped file.
    event.preventDefault();
    if (!disabled) setIsDragOver(true);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);
    if (disabled) return;

    // Dropping selected text or a link fires `drop` with an EMPTY file list.
    // Consumers post whatever they are handed, so without this guard a stray
    // text drag submits empty FormData to the field request (PR #1396 review).
    const files = [...event.dataTransfer.files];
    if (files.length === 0) return;

    onFiles(files);
  };

  const fileInput = (
    <input
      ref={inputRef}
      type="file"
      className="sr-only"
      accept={accept}
      multiple={multiple}
      disabled={disabled}
      aria-label={visibleLabel}
      data-test-id={inputTestId}
      {...buttonModeInputProps(asButton)}
      // Cleared after every pick so choosing the SAME file twice still fires
      // `change` — re-uploading a corrected file with the same name is a
      // real thing customers do.
      value=""
      onChange={(event) => onFiles([...(event.target.files ?? [])])}
      // The area's own handler would otherwise fire a second `click()` on
      // this input and open two pickers.
      onClick={(event) => event.stopPropagation()}
    />
  );

  if (asButton) {
    return (
      <div className={className}>
        {fileInput}
        <Button
          variant="ghost"
          size="sm"
          disabled={disabled}
          onClick={openPicker}
        >
          {CompactIcon && <CompactIcon className="mr-1.5" aria-hidden />}
          {/* Wrapped so the optional icon never inserts against a bare text
              node (.claude/rules/known-issues.md, browser translation). */}
          <span>{visibleLabel}</span>
        </Button>
      </div>
    );
  }

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: the file input inside is the labelled, focusable control; this element only widens its hit area
    <div
      className={cn(
        "flex w-full items-center gap-2 rounded-lg border-2 border-dashed text-center transition-colors",
        compact ? "flex-row justify-center p-3" : "flex-col p-5",
        // The focused control is the 1px sr-only input, whose own outline
        // cannot be seen — the area shows the keyboard focus for it.
        "has-[input:focus-visible]:border-primary has-[input:focus-visible]:ring-3 has-[input:focus-visible]:ring-primary/50",
        disabled
          ? "cursor-not-allowed opacity-50"
          : "cursor-pointer hover:border-primary hover:bg-primary-100",
        isDragOver
          ? "border-primary bg-primary-200"
          : "border-primary-200 bg-primary-150",
        className,
      )}
      onClick={openPicker}
      onDragOver={handleDragOver}
      onDragEnter={handleDragOver}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={handleDrop}
    >
      {fileInput}

      {compact ? (
        <>
          <CompactAreaIcon className="size-5 text-primary" aria-hidden />
          <span className="text-base-strong text-primary">{visibleLabel}</span>
        </>
      ) : (
        <>
          <Upload className="size-6 text-primary" aria-hidden />
          <span className="text-base-strong text-primary">{label}</span>
          {/* Dragging a file is a pointer gesture a touch device does not offer, so
            the phone copy only promises the tap. Done with CSS rather than a
            `useIsMobile` flip on purpose: a JS breakpoint would restructure this
            span on every resize, and a conditional element beside text here is
            the shape React crashes on under browser translation
            (.claude/rules/known-issues.md). `display:none` also keeps the
            hidden variant out of the accessibility tree, so only one is read. */}
          {hint ? (
            <span className="text-helper">{hint}</span>
          ) : (
            <>
              <span className="text-helper hidden md:inline">
                {t("iaq.fileUpload.dropHereHint")}
              </span>
              <span className="text-helper md:hidden">
                {t("iaq.fileUpload.dropHereHintTouch")}
              </span>
            </>
          )}
          {acceptedFileTypesMessage !== null && (
            <span className="text-helper">
              {acceptedFileTypesMessage ??
                t("iaq.fileUpload.acceptedFileTypesMessage")}
            </span>
          )}
        </>
      )}

      {children}
    </div>
  );
};
